import { useCallback, useEffect, useState } from 'react'
import {
  MailIcon,
  PencilIcon,
  TrashIcon,
  LoginIcon
} from '@heroicons/react/outline'

import { LoadingCircle } from 'components/Loading'
import { Pagination } from 'components/Pagination'
import { Attention } from './components/Attention'
import { UpdateUser } from './components/UpdateUser'
import { useConfirmationDialog } from 'components/ConfirmationDialog'
import { RowActionsDropdown } from 'components/RowActionsDropdown'
import { LoginAsModal } from 'modules/LoginAsClient/components/LoginAsModal'

import { User } from 'models'
import { classNames, phone } from 'utils'
import { ReadResult } from 'interfaces/queryOptions'
import { useEditClient } from 'modules/Clients/hooks/useEditClient'
import { SendEmail } from './components/SendEmail'

export function UsersTab() {
  const {
    isLoadingUsers,
    client,
    fetchClient,
    enterprise,
    fetchEnterprise,
    users,
    usersCount,
    listUsers,
    deleteUser
  } = useEditClient()

  const pageSize = 15
  const [page, setPage] = useState(1)

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isSendEmailOpen, setIsSendEmailOpen] = useState(false)
  const [isLoginAsOpen, setIsLoginAsOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<ReadResult<User> | null>(
    null
  )

  const [isEnterpriseCreated, setIsEnterpriseCreated] = useState(false)

  const [index, setIndex] = useState(0)

  const [showConfirmationDialog] = useConfirmationDialog()

  useEffect(() => {
    if (!client) {
      fetchClient()
    }
  }, [client, fetchClient])

  useEffect(() => {
    if (!client) return

    if (!enterprise) {
      fetchEnterprise()
    }
  }, [client, enterprise, fetchEnterprise])

  useEffect(() => {
    if (!client || !enterprise) return

    setIsEnterpriseCreated(client.relationships?.enterprise?.id && enterprise)
  }, [client, enterprise])

  useEffect(() => {
    if (!client || !enterprise) return

    listUsers(page)
  }, [client, enterprise, page])

  const onDelete = useCallback(
    async (id: number) => {
      if (!users) return

      setIndex(0)

      await deleteUser(id)
    },
    [deleteUser, users]
  )

  const onRemoveUser = (id: number) => {
    showConfirmationDialog({
      title: 'Remover usuário',
      message: 'Tem certeza que deseja deletar o usuário?',
      onConfirm: () => onDelete(id)
    })
  }

  return (
    <>
      <div className="w-full px-4 mt-6 animate-fade-in-down">
        {isLoadingUsers ? (
          <LoadingCircle />
        ) : !isEnterpriseCreated ? (
          <Attention />
        ) : (
          users &&
          users.length > 0 && (
            <div className="-mx-4 ring-1 ring-gray-300 sm:-mx-6 md:mx-0 md:rounded-lg animate-fade-in-down">
              <table className="min-w-full divide-y divide-gray-300">
                <tbody>
                  {users.map((user: ReadResult<User>, idx: number) => (
                    <tr key={idx}>
                      <td
                        className={classNames(
                          idx === 0 ? '' : 'border-t border-transparent',
                          'relative py-4 pl-4 sm:pl-6 pr-3 text-sm'
                        )}
                      >
                        <div className="font-medium text-gray-900">
                          {user.attributes.name}
                        </div>
                        {idx !== 0 ? (
                          <div className="absolute right-0 left-6 -top-px h-px bg-gray-200" />
                        ) : null}
                      </td>
                      <td
                        className={classNames(
                          idx === 0 ? '' : 'border-t border-gray-200',
                          'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell'
                        )}
                      >
                        {user.attributes.email}
                      </td>
                      <td
                        className={classNames(
                          idx === 0 ? '' : 'border-t border-gray-200',
                          'hidden px-3 py-3.5 text-sm text-gray-500 lg:table-cell'
                        )}
                      >
                        {phone(user.attributes.phone1)}
                      </td>
                      <td
                        className={classNames(
                          idx === 0 ? '' : 'border-t border-transparent',
                          'relative py-3.5 pl-3 pr-4 sm:pr-6 text-right text-sm font-medium'
                        )}
                      >
                        <RowActionsDropdown
                          actions={[
                            {
                              label: 'Editar',
                              icon: PencilIcon,
                              onClick: () => {
                                setIndex(idx)
                                setIsEditOpen(true)
                              }
                            },
                            {
                              label: 'Enviar email',
                              icon: MailIcon,
                              onClick: () => {
                                setIndex(idx)
                                setIsSendEmailOpen(true)
                              }
                            },
                            {
                              label: 'Login como cliente',
                              icon: LoginIcon,
                              onClick: () => {
                                setSelectedUser(user)
                                setIsLoginAsOpen(true)
                              }
                            },
                            {
                              label: 'Remover',
                              icon: TrashIcon,
                              onClick: () => onRemoveUser(idx),
                              show: users !== undefined && users.length > 1
                            }
                          ]}
                        />
                        {idx !== 0 ? (
                          <div className="absolute right-6 left-0 -top-px h-px bg-gray-200" />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
        {!isLoadingUsers && usersCount > pageSize && (
          <Pagination
            pageSize={pageSize}
            currentPage={page}
            itemsOnPage={users?.length ?? 0}
            totalItems={usersCount}
            onPageChange={setPage}
          />
        )}
      </div>

      <UpdateUser open={isEditOpen} setOpen={setIsEditOpen} index={index} />
      <SendEmail
        open={isSendEmailOpen}
        setOpen={setIsSendEmailOpen}
        index={index}
      />
      {selectedUser && (
        <LoginAsModal
          open={isLoginAsOpen}
          setOpen={setIsLoginAsOpen}
          user={selectedUser}
        />
      )}
    </>
  )
}
